import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Loader2, Save, User, Shield, ShieldCheck, ShieldOff } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { MFAEnrollDialog } from '@/components/MFAEnrollDialog';

interface MyProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MyProfileDialog({ open, onOpenChange }: MyProfileDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);

  useEffect(() => {
    if (open && user?.id) {
      loadProfile();
      checkMfaStatus();
    }
  }, [open, user?.id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, phone')
        .eq('id', user!.id)
        .single();

      if (error) throw error;
      setFullName(data?.full_name || '');
      setPhone(data?.phone || '');
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkMfaStatus = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      const hasVerifiedFactor = data.totp.some(f => f.status === 'verified');
      setMfaEnabled(hasVerifiedFactor);
    } catch (error) {
      console.error('Error checking MFA status:', error);
    }
  };

  const handleDisableMfa = async () => {
    try {
      setMfaLoading(true);
      const { data, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;

      const verifiedFactor = data.totp.find(f => f.status === 'verified');
      if (!verifiedFactor) return;

      const { error } = await supabase.auth.mfa.unenroll({ factorId: verifiedFactor.id });
      if (error) throw error;

      setMfaEnabled(false);
      toast({ title: '2FA Disabled', description: 'Two-factor authentication has been removed from your account.' });
    } catch (error: any) {
      console.error('Error disabling MFA:', error);
      toast({ title: 'Failed to disable 2FA', description: error.message, variant: 'destructive' });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }

    try {
      setSaving(true);
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim(),
          phone: phone.trim() || null,
        })
        .eq('id', user!.id);

      if (error) throw error;

      toast({ title: 'Profile updated successfully' });
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({ title: 'Failed to update profile', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              My Profile
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g. (555) 123-4567"
                  maxLength={20}
                />
                <p className="text-xs text-muted-foreground">
                  Used for internal communications only
                </p>
              </div>

              <Separator />

              {/* Two-Factor Authentication Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <Label>Two-Factor Authentication</Label>
                  </div>
                  {mfaEnabled ? (
                    <Badge variant="default" className="bg-green-600 hover:bg-green-600">
                      <ShieldCheck className="h-3 w-3 mr-1" />
                      Enabled
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      <ShieldOff className="h-3 w-3 mr-1" />
                      Disabled
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Add an extra layer of security with an authenticator app.
                </p>
                {mfaEnabled ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDisableMfa}
                    disabled={mfaLoading}
                    className="text-destructive hover:text-destructive"
                  >
                    {mfaLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Disable 2FA
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEnrollDialog(true)}
                  >
                    <Shield className="h-4 w-4 mr-2" />
                    Enable 2FA
                  </Button>
                )}
              </div>

              <Separator />

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <MFAEnrollDialog
        open={showEnrollDialog}
        onOpenChange={setShowEnrollDialog}
        onEnrolled={() => {
          setMfaEnabled(true);
          setShowEnrollDialog(false);
        }}
      />
    </>
  );
}
